import { Flex, FlexProps, Text, useToast } from '@chakra-ui/react';
import { ContentSearchPickerContentCard } from '~/components/ContentSearchPickerContentCard';
import { ContentSearchSkeleton } from '~/components/ContentSearchSkeleton';
import { ISearchContentResult } from '~/searchApi';

import {
  CONTENT_SEARCH_DEFAULT_LIMIT,
  CONTENT_SEARCH_DEFAULT_PAGE_SIZE,
  CONTENT_SEARCH_MAX_HEIGHT,
  CONTENT_SEARCH_WARNING_ID,
} from './ContentSearchPicker.constants';

export interface IContentSearchPickerProps extends Omit<FlexProps, 'onClick'> {
  contents: ISearchContentResult[];
  isLoading?: boolean;
  limit?: number;
  onClick: (content: ISearchContentResult) => void;
  selectedContent: ISearchContentResult[];
  showWarning?: boolean;
  skeletonItems?: number;
}

export const ContentSearchPicker: React.FC<IContentSearchPickerProps> = ({
  contents,
  isLoading,
  limit = CONTENT_SEARCH_DEFAULT_LIMIT,
  onClick,
  selectedContent,
  showWarning = false,
  skeletonItems = CONTENT_SEARCH_DEFAULT_PAGE_SIZE,
  ...flexProps
}) => {
  const toast = useToast();

  const handleClick = (content: ISearchContentResult) => {
    const isSelected = Boolean(
      selectedContent.find(({ contentId }) => contentId === content.contentId),
    );
    const isLimitReached = selectedContent.length >= limit;
    if (!isSelected && isLimitReached) {
      showWarning &&
        !toast.isActive(CONTENT_SEARCH_WARNING_ID) &&
        toast({
          description: `You have reached the maximum number of content selected`,
          id: CONTENT_SEARCH_WARNING_ID,
          status: 'warning',
          position: 'bottom-right',
          isClosable: true,
        });
      return;
    }
    onClick(content);
  };

  if (isLoading) {
    return (
      <ContentSearchSkeleton
        items={skeletonItems}
        maxHeight={CONTENT_SEARCH_MAX_HEIGHT}
      />
    );
  }

  if (contents.length) {
    return (
      <Flex
        track-id="content-search-picker"
        flexWrap="wrap"
        gap={2}
        sx={{
          ':after': {
            content: '""',
            flexGrow: 999,
          },
        }}
        {...flexProps}
      >
        {contents.map((content) => (
          <ContentSearchPickerContentCard
            key={content.contentId}
            contentData={content}
            isSelected={Boolean(
              selectedContent.find(
                ({ contentId }) => contentId === content.contentId,
              ),
            )}
            onClick={() => handleClick(content)}
            maxHeight={CONTENT_SEARCH_MAX_HEIGHT}
          />
        ))}
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      flexWrap="wrap"
      alignItems="center"
      paddingY={6}
    >
      <Text color="primary.400" fontSize="2xl" fontWeight={500}>
        Sorry, we couldn't find any results for your search
      </Text>
      <Text color="primary.300" fontSize="md">
        Please try again with different filters
      </Text>
    </Flex>
  );
};
